import { DataCenterFinancialSnapshot } from '../../financial/DataCenterFinancialSnapshot';
/**
 * FinancialSummarySection (FIN-2) — headline metrics, hero + rail (editorial).
 *
 * READ-ONLY over `report.financialAnalysis.headline` (the deterministic engine
 * stamp from projectFinance.recompute). W2a port: the hand-rolled KPI grid is
 * replaced by the shared editorial primitives — identical data, identical
 * formatting via financialFormat, identical hasData/empty semantics.
 *
 * K6 — per-type composition uplift. The three DECISION-DRIVING money metrics
 * (LCOE · NPV · IRR) are promoted to the signature `EditorialHeroMetric`
 * serif-numeral treatment (the redesign's "$4-7M" hero motif, re-skinned to OUR
 * theme tokens) so the money page LEADS with the numbers an investor reads
 * first. The remaining metrics (Payback · CAPEX/kW · Annual generation) stay in
 * the compact `EditorialKpiRail` beneath. NPV toned by sign; IRR toned vs the
 * hurdle and carrying the explicit "hurdle X%" sub-line. NEVER re-derives any
 * math; each hero/rail item is null-guarded so a thin stamp degrades cleanly.
 */
import React from 'react';
import type { SiteRagReport } from '../../../../types/siteRag';
import { SectionShell } from '../SectionShell';
import { lcoeBasisLabel, lcoeBasisNote } from '../../../../lib/lcoeBasis';
import { roleColor } from '../../../../lib/reportRoleColors';
import { useEffectiveReportTheme } from '../../../../lib/useEffectiveReportTheme';
import {
  EditorialKpiRail,
  EditorialHeroMetric,
  EditorialCallout,
  type EditorialKpiItem,
  type EditorialHeroTone,
} from '../../report/editorial';
import {
  fmtMwh,
  fmtPct,
  fmtUsdMillions,
  fmtUsdPerKw,
  fmtUsdPerMwh,
  fmtYears,
} from '../../../../lib/reportRendering/financialFormat';

/** A hero metric resolved from the engine stamp — dropped when its value is the
 *  em-dash sentinel (withheld), so the hero rail never fabricates a numeral. */
interface FinHero {
  label: string;
  value: string;
  sub?: string;
  tone: EditorialHeroTone;
}

const NA = '–';

export const FinancialSummarySection: React.FC<{ report: SiteRagReport; label: string }> = ({ report, label }) => {
  const theme = useEffectiveReportTheme();
  const h = report.financialAnalysis?.headline;
  const hasData = Boolean(h);

  const irrTone: EditorialHeroTone =
    h && typeof h.irrPct === 'number' && Number.isFinite(h.irrPct) &&
    typeof h.hurdleRatePct === 'number' && Number.isFinite(h.hurdleRatePct)
      ? (h.irrPct >= h.hurdleRatePct ? 'positive' : 'negative')
      : 'default';

  const npvTone: EditorialHeroTone =
    h && typeof h.npvUsd === 'number' && Number.isFinite(h.npvUsd)
      ? (h.npvUsd >= 0 ? 'positive' : 'negative')
      : 'default';

  const irrNote = h && typeof h.hurdleRatePct === 'number' && Number.isFinite(h.hurdleRatePct)
    ? `hurdle ${fmtPct(h.hurdleRatePct)}`
    : undefined;

  // Hero trio — LCOE / NPV / IRR. Each formatter already returns the em-dash
  // sentinel on a missing/non-finite value; we drop those so the hero strip
  // collapses to the resolved metrics only (no "—" hero numerals).
  const heroes: FinHero[] = h
    ? ([
        {
          label: 'LCOE',
          value: fmtUsdPerMwh(h.lcoeUsdPerMwh),
          // Q6 / 4.4 — NAME THE BASIS. A benchmark-pathway LCOE appears
          // elsewhere in the same report, and the two answer different
          // questions; without the label a reader reads the gap as a
          // discrepancy rather than a change of subject.
          sub: h.lcoeBasis ? lcoeBasisLabel(h.lcoeBasis) : undefined,
          tone: 'default',
        },
        { label: 'Net Present Value', value: fmtUsdMillions(h.npvUsd), tone: npvTone },
        { label: 'Project IRR', value: fmtPct(h.irrPct), sub: irrNote, tone: irrTone },
      ] satisfies FinHero[]).filter((hero) => hero.value !== NA)
    : [];

  // Secondary rail — the operating / cost metrics beneath the hero trio.
  const kpis: EditorialKpiItem[] = h
    ? [
        { key: 'Payback', value: fmtYears(h.paybackYears) },
        { key: 'CAPEX / kW', value: fmtUsdPerKw(h.capexPerKwUsd) },
        { key: 'Annual generation', value: fmtMwh(h.annualMwh) },
      ]
    : [];

  if (report.financialAnalysis?.modelKind === 'data-center-capacity-fee') return <SectionShell label={label} hasData><DataCenterFinancialSnapshot stamp={report.financialAnalysis} section="financialSummary" /></SectionShell>;

  return (
    <SectionShell label={label} hasData={hasData}>
      {h ? (
        // Carrier div holds the testid (the editorial primitives have none).
        <div data-testid="financial-summary-grid" className="space-y-4">
          {/* Illustrative-assumptions banner — shown when the engine was seeded
              from industry baselines because the site has no entered financials.
              Honest by construction: the numbers below are clearly framed as
              non-project-specific until the user enters their figures. */}
          {report.financialAnalysis?.illustrativeAssumptionsNote && (
            <div data-testid="financial-illustrative-banner">
              <EditorialCallout theme={theme} tone="warning" label="Illustrative figures">
                {report.financialAnalysis.illustrativeAssumptionsNote}
              </EditorialCallout>
            </div>
          )}
          {heroes.length > 0 && (
            <div
              className="grid grid-cols-1 sm:grid-cols-3 gap-x-8 gap-y-4"
              data-testid="financial-summary-hero"
            >
              {heroes.map((hero) => (
                <EditorialHeroMetric
                  key={hero.label}
                  theme={theme}
                  label={hero.label}
                  value={hero.value}
                  sub={hero.sub}
                  tone={hero.tone}
                  size="md"
                />
              ))}
            </div>
          )}
          <EditorialKpiRail theme={theme} items={kpis} columns={3} />
          {/* Q6 / 4.4 — say what the LCOE basis MEANS, not just its name. The
              same report carries a benchmark-pathway LCOE elsewhere; a reader
              who does not know the two answer different questions reads the gap
              between them as a discrepancy. */}
          {h?.lcoeBasis ? (
            <p
              className="text-[10px]"
              style={{ color: roleColor(theme, 'muted') }}
              data-testid="financial-lcoe-basis-note"
            >
              LCOE {lcoeBasisLabel(h.lcoeBasis)}. {lcoeBasisNote(h.lcoeBasis)}
            </p>
          ) : null}
        </div>
      ) : null}
    </SectionShell>
  );
};
